import React from "react";
import { motion } from "framer-motion";
import BtnAlevel from "../../buttons/BtnALevel";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import PageHeader from "../../components/PageHeader";
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../../components/ui/Card";
import { Badge } from "../../components/ui/Badge";
import EmptyState from "../../components/ui/EmptyState";
import BtnDeleteALevel from "../../buttons/DeleteALevel";
import moment from "../../helpers/moment";
import { FiBookOpen, FiAward } from "react-icons/fi";

const ALevel = ({ wizardMode = false }) => {
  const { isLoading, error, data } = useQuery({
    queryKey: ["aLevelResult"],
    queryFn: async () => {
      const req = await axiosget("ms_applicant_alevel");
      return req?.data;
    },
  });

  // Get grade variant for A-Level
  const getGradeVariant = (grade) => {
    const upperGrade = grade?.toUpperCase();
    if (["A", "B"].includes(upperGrade)) return "success";
    if (["C", "D"].includes(upperGrade)) return "info";
    if (["E"].includes(upperGrade)) return "warning";
    return "danger";
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
      },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
      },
    },
  };

  return (
    <>
      {!wizardMode ? (
        <PageHeader
          title="A-Level Results"
          subtitle="Create & delete A-Level result(s)"
          icon={<FiAward className="w-5 h-5" />}
          action={!isLoading && <BtnAlevel />}
        />
      ) : (
        !isLoading && <div className="flex justify-end mb-3"><BtnAlevel /></div>
      )}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Alert variant="danger"><AlertDescription>{`Error: ${error}`}</AlertDescription></Alert>
      ) : !data || data.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No A-Level Results Yet"
          description="You haven't added any A-Level results. Click the button below to add your A-Level examination details."
          icon={<FiBookOpen className="h-12 w-12" />}
        />
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6"
        >
          {data?.map((v, i) => {
            const r = v?.results || {};
            const subjects = v?.type === "Diploma"
              ? [{ name: r.courseOfStudy, grade: r.classification || r.cgpa }]
              : [
                  r.educationSubject && { name: r.educationSubject, grade: r.educationSubjectGrade },
                  r.educationSubject2 && { name: r.educationSubject2, grade: r.educationSubject2Grade },
                  r.educationSubject3 && { name: r.educationSubject3, grade: r.educationSubject3Grade },
                ].filter(Boolean);

            return (
              <motion.div key={`_alevel_result_${i}`} variants={cardVariants}>
                <Card variant="default" className="hover:shadow-lg transition-all duration-300 h-full">
                  <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 border-b border-purple-100 py-3 px-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <FiBookOpen className="h-4 w-4 text-purple-600" />
                        <CardTitle className="text-sm font-semibold">{v?.type}</CardTitle>
                      </div>
                      <div className="flex items-center gap-3">
                        <BtnDeleteALevel id={v._id} />
                        {r.yearOfGraduation && (
                          <span className="text-xs text-purple-600 font-medium">{r.yearOfGraduation}</span>
                        )}
                      </div>
                    </div>
                    {r.institutionName && (
                      <p className="text-xs text-neutral-500 mt-1 ml-6 truncate">{r.institutionName}</p>
                    )}
                  </CardHeader>

                  <CardContent className="p-0">
                    {/* Subjects table */}
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-neutral-100">
                          <th className="text-left text-xs font-medium text-neutral-500 px-4 py-2">Subject</th>
                          <th className="text-right text-xs font-medium text-neutral-500 px-4 py-2">Grade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {subjects.map((sub, j) => (
                          <tr key={j} className="border-b border-neutral-50 last:border-0">
                            <td className="px-4 py-2 font-medium text-neutral-800">{sub.name}</td>
                            <td className="px-4 py-2 text-right">
                              <Badge variant={getGradeVariant(sub.grade)} className="font-bold">
                                {sub.grade}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                        {v?.type === "Diploma" && r.cgpa && (
                          <tr className="border-b border-neutral-50 last:border-0">
                            <td className="px-4 py-2 font-medium text-neutral-800">CGPA</td>
                            <td className="px-4 py-2 text-right">
                              <span className="text-sm font-bold text-purple-700">{r.cgpa}</span>
                            </td>
                          </tr>
                        )}
                        {v?.type === "Diploma" && r.diplomaType && (
                          <tr className="border-b border-neutral-50 last:border-0">
                            <td className="px-4 py-2 font-medium text-neutral-800">Type</td>
                            <td className="px-4 py-2 text-right text-neutral-600">{r.diplomaType}</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </CardContent>

                  <CardFooter className="py-2 px-4 bg-neutral-50 border-t border-neutral-100">
                    <p className="text-xs text-neutral-400 w-full text-center">
                      Added {moment(v.createdAt)} ago
                    </p>
                  </CardFooter>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      )}

    </>
  );
};

export default ALevel;
